import type { DaypartT, EnergyT, PriorityT, Recurrence } from '@portfolio-pm/shared';
import type { ParseSuccess } from '@portfolio-pm/smart-add';
import type { CreateTaskInput } from '@portfolio-pm/yjs-doc';

/**
 * Bridge SmartAdd parser output to a CreateTaskInput the yjs-doc package can
 * consume. SmartAdd's Priority is 1|2|3; shared's PriorityT is 0|1|2|3, so an
 * absent priority maps to 0 (default).
 *
 * Kept in apps/web because it is application glue — the parser is purely
 * lexical, the doc helpers are purely storage, and this is the piece that
 * knows "the user typed a line in the quick-add box".
 */
export interface SmartAddCreateOptions {
  defaultScheduledAt?: number;
}

export function smartAddToCreateInput(
  parsed: ParseSuccess,
  projectId: string,
  options: SmartAddCreateOptions = {},
): CreateTaskInput {
  const input: CreateTaskInput = {
    title: parsed.title,
    projectId,
    priority: (parsed.priority ?? 0) as PriorityT,
    tags: [...parsed.tags],
  };
  if (parsed.dueAt !== undefined) {
    input.dueAt = parsed.dueAt;
    input.dueAtHasTime = parsed.dueAtHasTime ?? false;
  }
  if (parsed.scheduledAt !== undefined) {
    input.scheduledAt = parsed.scheduledAt;
    input.scheduledAtHasTime = parsed.scheduledAtHasTime ?? false;
  }
  if (parsed.scheduledAt === undefined && options.defaultScheduledAt !== undefined) {
    input.scheduledAt = options.defaultScheduledAt;
    input.scheduledAtHasTime = false;
  }
  if (parsed.estimateMinutes !== undefined) input.estimateMinutes = parsed.estimateMinutes;
  if (parsed.energy !== undefined) input.energy = parsed.energy as EnergyT;
  if (parsed.daypart !== undefined) input.daypart = parsed.daypart as DaypartT;
  if (parsed.location !== undefined) input.location = parsed.location;
  if (parsed.owner !== undefined) input.owner = parsed.owner;
  if (parsed.notes !== undefined) input.notes = parsed.notes;
  if (parsed.recurrence !== undefined) {
    const recurrence: Recurrence = {
      rrule: parsed.recurrence.rrule,
      fromCompletion: parsed.recurrence.fromCompletion,
    };
    input.recurrence = recurrence;
  }
  return input;
}
